/**
 * Consent and opt-out — phases.md Phase 16: "respect Do Not Track and Global Privacy
 * Control; add a lightweight cookie/consent notice appropriate to the jurisdiction".
 *
 * The jurisdiction is India, and the applicable law is the Digital Personal Data
 * Protection Act 2023, which is a consent statute: notice in clear language, a genuine
 * choice, and withdrawal as easy as giving it. What it is *not* is the EU's cookie
 * banner, and copying one would be worse than useless — a wall of toggles in front of an
 * Assamese news site that stores no cookie and no identifier would be theatre.
 *
 * So the notice here is one line, two buttons, and no dark pattern: decline is the same
 * size as accept, and declining is remembered as firmly as accepting.
 *
 * Three states, and the difference between them matters:
 *
 * - **Opted out** — DNT or GPC is set, or the reader declined. Nothing is measured, and
 *   the notice is not shown to someone whose browser already answered it.
 * - **Undecided** — the notice is showing. Measurement is *already running*, because
 *   what it measures at this point is a page view with no identifier attached to a
 *   person, which the DPDPA does not require consent for and which a newsroom needs in
 *   order to know anyone was there at all.
 * - **Accepted** — the notice is gone and stays gone.
 *
 * That middle state is a judgement call and worth stating plainly: measuring before an
 * answer is defensible here precisely because the pipeline behind it cannot identify
 * anybody. If this site ever stores something durable per reader, the default has to
 * flip, and this comment is the note to whoever does it.
 */

const STORAGE_KEY = 'nk:consent';

export type ConsentState = 'accepted' | 'declined' | 'undecided';

/** The browser-level signals, checked before anything this site itself has stored. */
export function browserOptsOut(): boolean {
  if (typeof navigator === 'undefined') return false;

  const nav = navigator as Navigator & { globalPrivacyControl?: boolean; msDoNotTrack?: string };
  const win =
    typeof window === 'undefined' ? undefined : (window as Window & { doNotTrack?: string });

  // `doNotTrack` has lived on three different objects across browsers, and reading only
  // the modern one silently ignores the signal in the browsers most likely to send it.
  const dnt = nav.doNotTrack ?? win?.doNotTrack ?? nav.msDoNotTrack;
  return dnt === '1' || dnt === 'yes' || nav.globalPrivacyControl === true;
}

export function readConsent(): ConsentState {
  if (browserOptsOut()) return 'declined';
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'accepted' || stored === 'declined' ? stored : 'undecided';
  } catch {
    // Blocked storage: the notice reappears next visit, which is the safe direction —
    // a reader who cannot be asked again is a reader who cannot withdraw.
    return 'undecided';
  }
}

export function writeConsent(state: Exclude<ConsentState, 'undecided'>): void {
  try {
    localStorage.setItem(STORAGE_KEY, state);
  } catch {
    // The choice still holds for this page; it just will not be remembered.
  }
  for (const listener of listeners) listener(state);
}

/** True when events may be sent: everything except an explicit or browser-level refusal. */
export function measurementAllowed(): boolean {
  return readConsent() !== 'declined';
}

const listeners = new Set<(state: ConsentState) => void>();

/** Lets the tracker stop the moment a reader declines, without a reload. */
export function onConsentChange(listener: (state: ConsentState) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
